import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { Component, DestroyRef, HostBinding, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { RippleModule } from 'primeng/ripple';
import { LayoutService } from '../../services/layout.service';

@Component({
  selector: '[igx-menu-item]',
  imports: [RouterModule, RippleModule, NgClass],
  templateUrl: './menu-item.component.html',
  styleUrl: './menu-item.component.scss',
  animations: [
    trigger('children', [
      state(
        'collapsed',
        style({
          height: '0',
        })
      ),
      state(
        'expanded',
        style({
          height: '*',
        })
      ),
      transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
    ]),
  ],
})
export class MenuItemComponent {
  public router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _layoutService = inject(LayoutService);

  public item = input.required<MenuItem>();
  public index = input.required<number>();
  public parentKey = input<string>();

  @HostBinding('class.menu-item__root')
  public root = input<boolean>(false);

  public active = signal(false);

  public menuSourceSubscription = this._layoutService.menuSource$
    .pipe(takeUntilDestroyed(this._destroyRef))
    .subscribe((value) => {
      Promise.resolve(null).then(() => {
        if (value.routeEvent) {
          this.active.update(() => value.key === this.key || value.key.startsWith(this.key + '-'));
        } else if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
          this.active.update(() => false);
        }
      });
    });

  public menuResetSubscription = this._layoutService.resetSource$
    .pipe(takeUntilDestroyed(this._destroyRef))
    .subscribe(() => {
      this.active.update(() => false);
    });

  key: string = '';

  updateActiveStateFromRoute() {
    let activeRoute = this.router.isActive(this.item().routerLink[0], {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored',
    });

    if (activeRoute) {
      this._layoutService.onMenuStateChange({ key: this.key, routeEvent: true });
    }
  }

  public itemClick(event: Event) {
    // avoid processing disabled items
    if (this.item().disabled) {
      event.preventDefault();
      return;
    }

    // execute command
    if (this.item().command) {
      this.item().command({ originalEvent: event, item: this.item });
    }

    // toggle active state
    if (this.item().items) {
      this.active.update((active) => !active);
    }

    this._layoutService.onMenuStateChange({ key: this.key });
  }

  public get submenuAnimation() {
    return this.root() ? 'expanded' : 'collapsed';
  }

  @HostBinding('class.menu-item--active')
  public get activeClass() {
    return this.active() && !this.root();
  }
}
